import { EcardSendFormData } from '@dotcom/crown-ui';
import { QueryClient } from '@tanstack/react-query';
import { validateNames } from '../../../components/address-form/address-utils';
import { DigitalAddress } from '../../../global-types/addresses';
import { DigitalOrderSubmission } from '../../../global-types/digital-orders';
import { accountProfileQueryOptions } from '../../../services/customization';
import {
  recipientsQueryOptions,
  saveRecipient,
  saveRecipientAssets,
  updateRecipient,
} from '../../../services/recipients';
import { loadProjectDetails } from '../../../utils/digital-greetings/load-project-details';

export type PreparedEcardRecipient = {
  projectId: string;
  address: DigitalAddress;
  frontUrl: string;
  submission: DigitalOrderSubmission;
};

type EcardSenderForm = Pick<EcardSendFormData, 'senderFirstName' | 'senderLastName'>;

const getLinkSenderName = (value: string | undefined, maxLength: number, placeholder: string): string => {
  const name = value?.trim() ?? '';
  return name.length >= 2 && name.length <= maxLength && validateNames(name, (key) => key) === true
    ? name
    : placeholder;
};

/**
 * Creates or updates an Email (`E`) or Message (`L`) recipient and links the
 * saved card panels. Omitting `form` selects Message and sends the facade's
 * required null contact value. Link delivery uses valid supplied sender names,
 * with undisplayed placeholders for missing or invalid names.
 *
 * @returns Recipient data containing the facade-generated share URL.
 */
export const prepareEcardRecipient = async (
  projectId: string,
  queryClient: QueryClient,
  form?: EcardSendFormData,
  messageSender?: EcardSenderForm,
): Promise<PreparedEcardRecipient> => {
  if (!projectId) throw new Error('Project ID is required to prepare an eCard');

  const panels = await loadProjectDetails(projectId, queryClient);
  const frontUrl = panels.F?.url || panels.F?.resize_urls.M;
  const insideUrl = panels.I?.url || panels.I?.resize_urls.M;
  if (!frontUrl || !insideUrl) throw new Error('Both saved eCard panels are required');

  const profile = await queryClient.fetchQuery(accountProfileQueryOptions());
  if (!profile?.email_address) throw new Error('Sender profile is unavailable');

  const sender = {
    sender_first_name: form
      ? form.senderFirstName.trim()
      : getLinkSenderName(messageSender?.senderFirstName, 15, 'Mobile'),
    sender_last_name: form ? form.senderLastName.trim() : getLinkSenderName(messageSender?.senderLastName, 20, 'Text'),
    sender_email: profile.email_address.trim(),
  };
  const recipientTypeCode = form ? 'E' : 'L';
  const payload = form
    ? {
        ...sender,
        recipient_type_code: 'E' as const,
        recipient_first_name: form.recipientFirstName.trim(),
        recipient_last_name: form.recipientLastName.trim(),
        contact_info: form.recipientEmail.trim(),
      }
    : {
        ...sender,
        // Requires the example's name placeholders even for link recipients.
        recipient_first_name: 'Mobile',
        recipient_last_name: 'Text',
        contact_info: null,
        recipient_type_code: 'L' as const,
      };

  const recipientOptions = recipientsQueryOptions(projectId);
  const recipients = await queryClient.fetchQuery(recipientOptions);
  if (recipients.data !== null && !Array.isArray(recipients.data)) {
    throw new Error('Recipient response is incomplete');
  }
  const existing = recipients.data?.find((recipient) => recipient.recipient_type_code === recipientTypeCode);
  let recipientId = existing?.recipient_id;
  let assets: Awaited<ReturnType<typeof saveRecipientAssets>>;
  try {
    if (recipientId) {
      await updateRecipient(projectId, recipientId, payload);
    } else {
      const response = await saveRecipient(projectId, payload);
      recipientId = response.data?.recipient_id;
    }
    if (!recipientId) throw new Error('Recipient response is missing its ID');

    assets = await saveRecipientAssets(projectId, { front_url: frontUrl, inside_url: insideUrl }, recipientId);
  } finally {
    await queryClient.invalidateQueries({ queryKey: recipientOptions.queryKey });
  }
  const url = assets.data?.url?.trim();
  if (!url || !['https:', 'http:'].includes(new URL(url).protocol)) {
    throw new Error('Recipient assets response is missing a valid share URL');
  }

  return {
    projectId,
    frontUrl,
    address: {
      ...sender,
      recipient_first_name: form?.recipientFirstName.trim() ?? '',
      recipient_last_name: form?.recipientLastName.trim() ?? '',
      contact_info: form?.recipientEmail.trim() ?? '',
      recipient_type_code: recipientTypeCode,
      url,
    },
    submission: {},
  };
};
